import { PaymentMethodPayload } from 'braintree-web-drop-in';
import API from '../services/AxiosInstance';
import { SavedPaymentMethodDetails } from '../services/renewalsApi';
import {
	Address,
	NewPaymentMethodDetails,
	PaymentClient,
	PaymentOption,
	PaymentProvider,
	Product,
	RenewalsClient,
	TakeUpBraintreeRenewalCommand,
} from '../services/renewalsApiV2';
import { FullPolicy } from '../services/api';
import { ICreateNewRenewalVersionResponse } from '../store/renewal.slice';
import { formatISO } from 'date-fns';

interface IProps {
	policy: FullPolicy;
	renewal: ICreateNewRenewalVersionResponse;
}

interface ITakeUpRenewalParams {
	paymentOption: PaymentOption;
	sendPostalDocuments: boolean;
	product: Product;
	isFutureStartDate: boolean;
	startDate?: string;
	paymentId?: string;
	idempotencyKey?: string;
	braintreePaymentDetails?: PaymentMethodPayload;
	balanceSettlementDetails?: SavedPaymentMethodDetails;
	newBalanceSettlementDetails?: NewPaymentMethodDetails;
	existingCardPaymentMethodId?: string;
}

const useBraintreeRenewalPayment = ({ policy, renewal }: IProps) => {
	const getPaymentIntent = async (amount: number, saveCard: boolean, address: Address) => {
		const client = new PaymentClient(undefined, API);

		return await client.makePaymentIntent(PaymentProvider.BRAINTREE, {
			policyToken: policy.policyToken,
			saveCard,
			currencyCode: 'GBP',
			amount,
			billingAddress: { ...address, country: 'GB' },
			product: policy.product as Product,
		});
	};

	const takeUpRenewal = async ({
		paymentOption,
		isFutureStartDate,
		startDate,
		paymentId,
		idempotencyKey,
		braintreePaymentDetails,
		balanceSettlementDetails,
		newBalanceSettlementDetails,
		existingCardPaymentMethodId,
		sendPostalDocuments,
	}: ITakeUpRenewalParams) => {
		if (!renewal.renewalRatingResponse?.underwrittenOptionId) {
			throw new Error('No rate');
		}

		if (!startDate) {
			throw new Error('Start date is required');
		}

		const client = new RenewalsClient(undefined, API);
		const transactionId = renewal.newRenewalTransactionId || (policy.renewalDetails?.transactionId as string);

		const takeUpCommand: TakeUpBraintreeRenewalCommand = {
			policyToken: policy.policyToken,
			transactionId,
			underwrittenOptionId: renewal.renewalRatingResponse.underwrittenOptionId,
			paymentOption,
			isFutureStartDate,
			startDate: formatISO(startDate),
			sendPostalDocuments,
			...(braintreePaymentDetails &&
				paymentId && {
					idempotencyKey,
					upFrontSettlementDetails: {
						paymentId,
						nonce: braintreePaymentDetails.nonce,
						deviceData: braintreePaymentDetails.deviceData,
					},
				}),
			...(paymentOption !== PaymentOption.PayInFull &&
				balanceSettlementDetails && {
					savedBalanceSettlementDetails: balanceSettlementDetails,
				}),
			...(newBalanceSettlementDetails && {
				newBalanceSettlementDetails,
			}),
			...(existingCardPaymentMethodId && {
				existingCardPaymentMethodId,
			}),
		};

		return await client.takeUp(takeUpCommand);
	};

	return {
		getPaymentIntent,
		takeUpRenewal,
	};
};

export default useBraintreeRenewalPayment;
